import { type CSSProperties } from 'react'

import { cn } from '../cn'

// Spinner / Skeleton / ProgressBar (design/components/loading.html). Three ways
// to say "working": Skeleton (preferred for known-shape content — preserves
// layout), Spinner (short shapeless waits), ProgressBar (a REAL percentage —
// never an invented one; unknown progress is indeterminate). All respect
// prefers-reduced-motion; the percentage is shown as text, not bar length only.

const spinnerSize = {
  sm: 'size-[14px] border-[1.5px]',
  md: 'size-[18px] border-2',
  lg: 'size-7 border-[3px]',
}

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  label?: string
  className?: string
}

/** Spinning ring for short, shapeless waits; role=status with either a visible
 *  label or an sr-only "Cargando…". For known-shape content prefer Skeleton.
 *  Honours prefers-reduced-motion. */
export function Spinner({ size = 'md', label, className }: SpinnerProps) {
  return (
    <span
      role="status"
      className={cn('inline-flex items-center gap-[9px] text-[12px] text-text-2', className)}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block animate-spin rounded-full border-field border-t-brand-blue [animation-duration:0.65s] motion-reduce:animate-none',
          spinnerSize[size],
        )}
      />
      {label ? <span>{label}</span> : <span className="sr-only">Cargando…</span>}
    </span>
  )
}

const skelBase =
  'block animate-shimmer rounded-[5px] bg-[linear-gradient(90deg,var(--field)_0%,var(--hover)_50%,var(--field)_100%)] bg-[length:200%_100%] motion-reduce:animate-none'

const skelVariant = {
  line: 'h-[9px] w-full',
  title: 'h-[14px] w-3/5',
  avatar: 'size-9 rounded-full',
  block: 'h-full w-full rounded-lg',
}

export interface SkeletonProps {
  variant?: 'line' | 'title' | 'avatar' | 'block'
  width?: string
  height?: string
  className?: string
}

/** Shimmer placeholder that preserves the coming content's layout. aria-hidden
 *  — the surrounding container must announce loading. `width`/`height`
 *  override the variant's box. */
export function Skeleton({ variant = 'line', width, height, className }: SkeletonProps) {
  const style: CSSProperties = {}
  if (width) style.width = width
  if (height) style.height = height
  return (
    <span aria-hidden className={cn(skelBase, skelVariant[variant], className)} style={style} />
  )
}

const progTone = {
  brand: 'bg-brand-blue',
  ok: 'bg-ok',
  warn: 'bg-warn',
  sens: 'bg-sens',
}

export interface ProgressBarProps {
  /** 0–100; omit for an indeterminate bar. */
  value?: number
  tone?: 'brand' | 'ok' | 'warn' | 'sens'
  label?: string
  valueText?: string
  className?: string
}

/** role=progressbar whose percentage is also shown as `valueText`, never bar
 *  length alone. Only feed a REAL 0–100 value (clamped) — omit `value` for the
 *  indeterminate animation. */
export function ProgressBar({
  value,
  tone = 'brand',
  label,
  valueText,
  className,
}: ProgressBarProps) {
  const indeterminate = value == null
  const pct = indeterminate ? 0 : Math.min(100, Math.max(0, value))
  return (
    <div className={cn('w-full', className)}>
      {(Boolean(label) || Boolean(valueText)) && (
        <div className="mb-1.5 flex items-center justify-between text-[11px]">
          {label && <span className="font-medium text-text-2">{label}</span>}
          {valueText && <span className="tabular-nums text-text-3">{valueText}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        aria-valuetext={valueText}
        aria-label={label ?? 'Progreso'}
        className="h-[7px] w-full overflow-hidden rounded-[4px] bg-field"
      >
        {indeterminate ? (
          <span
            className={cn(
              'block h-full w-2/5 animate-indeterminate rounded-[4px] motion-reduce:animate-none',
              progTone[tone],
            )}
          />
        ) : (
          <span
            className={cn(
              'block h-full rounded-[4px] transition-[width] duration-300 ease-vysto',
              progTone[tone],
            )}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  )
}
